import React, { useState, useEffect } from 'react';
import { X, Zap, Building2, AlertCircle, Shield, CheckCircle, Loader2, ArrowRight } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import paymentService from '../../services/paymentService';
import { useAuth } from '../../contexts/AuthContext';

const PRESETS = [20, 50, 100, 200, 500];

// Same rule as the server: a number or numeric string, finite, between 0 and 10%; otherwise
// the 2.5% default. `??` (not `||`) so an admin-configured 0 shows as 0%.
const DEFAULT_ONLINE_FEE_RATE = 0.025;
const normaliseOnlineFeeRate = (value) => {
  const raw = value ?? DEFAULT_ONLINE_FEE_RATE;
  const rate = typeof raw === 'number'
    ? raw
    : (typeof raw === 'string' && raw.trim() !== '' ? Number(raw) : NaN);
  return Number.isFinite(rate) && rate >= 0 && rate <= 0.1 ? rate : DEFAULT_ONLINE_FEE_RATE;
};

export default function TopUpModal({ isOpen, onClose, onSuccess, onRequirePaymentMethod }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState('amount'); // 'amount' | 'method' | 'manual' | 'processing'
  const [amount, setAmount] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [method, setMethod] = useState('online'); // 'online' | 'manual'
  const [manualRef, setManualRef] = useState('');
  const [manualDate, setManualDate] = useState('');
  const [loading, setLoading] = useState(false);

  const [settings, setSettings] = useState({
    manual_payment_enabled: false,
    pay_online_enabled: true,
    manual_payment_fee_percentage: 0.015,
    pay_online_fee_percentage: 0.025,
    manual_bank_options: []
  });
  const [selectedBankOptionId, setSelectedBankOptionId] = useState('');

  useEffect(() => {
    if (isOpen) {
      paymentService.getPublicSettings().then(res => {
        if (res.success) {
          const config = res.data;
          setSettings({
            manual_payment_enabled: config.manual_payment_enabled,
            pay_online_enabled: config.pay_online_enabled !== false,
            manual_payment_fee_percentage: config.manual_payment_fee_percentage || 0.015,
            pay_online_fee_percentage: normaliseOnlineFeeRate(config.pay_online_fee_percentage),
            manual_bank_options: config.manual_bank_options || []
          });
          if (config.manual_bank_options?.length > 0) {
            setSelectedBankOptionId(config.manual_bank_options[0].id);
          }
          // Default to manual if online is disabled
          if (config.pay_online_enabled === false && config.manual_payment_enabled) {
            setMethod('manual');
          }
        }
      }).catch(console.error);
    } else {
      setStep('amount');
      setAmount('');
      setCustomAmount('');
      setManualRef('');
      setManualDate('');
    }
  }, [isOpen]);

  const isSecondaryVerified =
    user?.verificationLevel === 'secondary' || user?.verificationLevel === 'full';

  const hasPaymentMethod = user?.paymentMethods?.length > 0;

  const finalAmount = parseFloat(amount || customAmount || '0');
  const balance = user?.wallet?.availableBalance || 0;

  const currentFeePercentage = method === 'manual' ? settings.manual_payment_fee_percentage : settings.pay_online_fee_percentage;
  const totalAmountToPay = finalAmount + (finalAmount * currentFeePercentage);
  
  const selectedBankOption = settings.manual_bank_options.find(opt => opt.id === selectedBankOptionId);

  const selectPreset = (val) => {
    setAmount(String(val));
    setCustomAmount('');
  };

  const handleCustom = (v) => {
    setCustomAmount(v);
    setAmount('');
  };

  const proceedToMethod = () => {
    if (finalAmount < 10) { toast.error('Minimum deposit is GH₵ 10'); return; }
    
    // Auto-skip method selection if only one method is enabled
    if (settings.pay_online_enabled && !settings.manual_payment_enabled) {
      setMethod('online');
    } else if (!settings.pay_online_enabled && settings.manual_payment_enabled) {
      setMethod('manual');
    }
    
    setStep('method');
  };

    const handleOnlinePayment = async () => {
    setLoading(true);
    setStep('processing');
    try {
      const res = await paymentService.addFunds({
        amount: finalAmount,
        currency: 'GHS',
        email: user.email,
      });

      const data = res?.data || res;
      // Extract specific data from the nested 'data' object returned by service
      const backendPayload = data?.data || data; 
      const usedProcessor = backendPayload?.processor; 
      const authUrl = backendPayload?.authorization_url || backendPayload?.link;
      const reference = backendPayload?.reference;
      // Charge exactly what the server initialised (load + fee); never recompute it here.
      const totalCharge = Number(backendPayload?.totalCharge);
      const hasServerTotal = Number.isFinite(totalCharge) && totalCharge > 0;
      const serverCredit = backendPayload?.amountToCredit != null ? Number(backendPayload.amountToCredit) : NaN;
      const creditedAmount = Number.isFinite(serverCredit) ? serverCredit : finalAmount;

      // Only use Paystack Inline if the backend actually used Paystack and told us the total.
      // Without a server total, the redirect below carries the server's amount instead.
      if (usedProcessor === 'paystack' && window.PaystackPop && reference && hasServerTotal) {
        const handler = window.PaystackPop.setup({
          key: import.meta.env.VITE_PAYSTACK_PUBLIC_KEY,
          email: user.email,
          amount: Math.round(totalCharge * 100), // server's total, in pesewas/kobo
          currency: 'GHS',
          ref: reference,
          callback: function (response) {
            paymentService.verifyPayment({ reference: response.reference })
              .then(() => {
                toast.success(`GH₵${creditedAmount.toFixed(2)} added to your wallet!`);
                if (onSuccess) onSuccess();
                onClose();
              })
              .catch(() => {
                toast.error('Payment verification failed. It will be confirmed shortly.');
                onClose();
              });
          },
          onClose: () => {
            setStep('method');
            setLoading(false);
          },
        });
        handler.openIframe();
        return;
      }

      // Fallback to Redirect (Works for Flutterwave or if Paystack Inline isn't loaded)
      if (authUrl) {
        window.location.href = authUrl;
        return;
      }

      throw new Error('Could not determine payment gateway link');
    } catch (err) {
      toast.error(err?.message || 'Failed to start payment');
      setStep('method');
    } finally {
      setLoading(false);
    }
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (!manualRef || !manualDate) { toast.error('Reference and date are required'); return; }
    if (!selectedBankOption) { toast.error('Select a transfer method'); return; }
    
    setLoading(true);
    try {
      await paymentService.submitManualDeposit({
        amount: finalAmount,
        reference: manualRef,
        date: manualDate,
        method: 'bank_transfer',
        metadata: {
            bankOption: selectedBankOption.label,
            totalPaid: totalAmountToPay,
            feePercentage: settings.manual_payment_fee_percentage
        }
      });
      toast.success('Manual deposit submitted for review. We\'ll credit your wallet upon confirmation.');
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err?.message || 'Failed to submit deposit');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}>
      <div className="relative w-full max-w-md rounded-2xl overflow-hidden shadow-2xl" style={{ background: '#fff' }}>

        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg, #1a56db 0%, #6c3afc 100%)', padding: '24px 24px 20px' }}>
          <button onClick={onClose} className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors">
            <X size={20} />
          </button>
          <p className="text-white/70 text-sm font-medium mb-1">Current Balance</p>
          <p className="text-white text-3xl font-bold">GH₵ {balance.toFixed(2)}</p>
        </div>

        <div className="p-6">
          {/* Verification gate */}
          {!isSecondaryVerified ? (
            <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-start gap-3">
                <Shield size={18} className="text-amber-600 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-amber-900">Secondary Verification Required</p>
                  <p className="text-xs text-amber-700 mt-1">Please verify your identity to unlock deposits and withdrawals.</p>
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => { onClose(); navigate('/dashboard/settings?tab=verification'); }}
                      className="text-xs font-semibold bg-amber-600 text-white px-3 py-1.5 rounded-lg hover:bg-amber-700 transition-colors">
                      Verify Identity
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : !hasPaymentMethod ? (
            <div className="mb-5 rounded-xl border border-blue-200 bg-blue-50 p-4">
              <div className="flex items-start gap-3">
                <Shield size={18} className="text-blue-600 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-blue-900">Payment Method Required</p>
                  <p className="text-xs text-blue-700 mt-1">You must add a verified payment method in your real name to enable top-ups.</p>
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => { 
                        onClose(); 
                        if (onRequirePaymentMethod) onRequirePaymentMethod();
                        else navigate('/dashboard/settings?tab=verification'); 
                      }}
                      className="text-xs font-semibold bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 transition-colors">
                      Add Payment Method
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {/* STEP: amount */}
          {isSecondaryVerified && hasPaymentMethod && step === 'amount' && (
            <div className="space-y-4">
              <p className="text-sm font-semibold text-gray-700">Select Amount</p>
              <div className="grid grid-cols-3 gap-2">
                {PRESETS.map((p) => (
                  <button key={p} onClick={() => selectPreset(p)}
                    className="rounded-xl border-2 py-2.5 text-sm font-bold transition-all"
                    style={{
                      borderColor: amount === String(p) ? '#1a56db' : '#e5e7eb',
                      background: amount === String(p) ? '#eff6ff' : '#f9fafb',
                      color: amount === String(p) ? '#1a56db' : '#374151',
                    }}>
                    GH₵{p}
                  </button>
                ))}
                <div className="relative rounded-xl border-2 flex items-center transition-all"
                  style={{ borderColor: customAmount ? '#1a56db' : '#e5e7eb', background: customAmount ? '#eff6ff' : '#f9fafb' }}>
                  <span className="pl-2 text-sm text-gray-500 font-medium">GH₵</span>
                  <input type="number" placeholder="Other" value={customAmount}
                    onChange={(e) => handleCustom(e.target.value)} min={10}
                    className="w-full bg-transparent text-sm font-bold text-gray-800 outline-none px-1 py-2.5"
                    style={{ color: customAmount ? '#1a56db' : '' }} />
                </div>
              </div>
              
              <button onClick={proceedToMethod} disabled={finalAmount < 10 || (!settings.pay_online_enabled && !settings.manual_payment_enabled)}
                className="w-full py-3 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2 transition-all"
                style={{ background: finalAmount >= 10 ? 'linear-gradient(135deg,#1a56db,#6c3afc)' : '#d1d5db', cursor: finalAmount >= 10 ? 'pointer' : 'not-allowed' }}>
                Continue <ArrowRight size={16} />
              </button>
              
              {!settings.pay_online_enabled && !settings.manual_payment_enabled && (
                <p className="text-center text-xs text-red-500 font-medium mt-2">All top-up channels are currently disabled by the admin.</p>
              )}
            </div>
          )}

          {/* STEP: method */}
          {step === 'method' && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <button onClick={() => setStep('amount')} className="text-sm text-blue-600 hover:underline">← Back</button>
                <span className="text-sm text-gray-400">Adding GH₵{finalAmount.toFixed(2)}</span>
              </div>
              <p className="text-sm font-semibold text-gray-700 mb-2">Choose Payment Method</p>

              {settings.pay_online_enabled && (
                <button onClick={() => setMethod('online')}
                  className="w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left"
                  style={{ borderColor: method === 'online' ? '#1a56db' : '#e5e7eb', background: method === 'online' ? '#eff6ff' : '#f9fafb' }}>
                  <div className="p-2 rounded-lg bg-blue-100"><Zap size={20} className="text-blue-600" /></div>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-gray-900">Pay Online</p>
                    <p className="text-xs text-gray-500">Instant · Card, MoMo, USSD via Paystack</p>
                  </div>
                  {method === 'online' && <CheckCircle size={18} className="text-blue-600 shrink-0" />}
                </button>
              )}

              {settings.manual_payment_enabled && (
                <button onClick={() => setMethod('manual')}
                  className="w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left"
                  style={{ borderColor: method === 'manual' ? '#1a56db' : '#e5e7eb', background: method === 'manual' ? '#eff6ff' : '#f9fafb' }}>
                  <div className="p-2 rounded-lg bg-indigo-100"><Building2 size={20} className="text-indigo-600" /></div>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-gray-900">Manual Bank Transfer</p>
                    <p className="text-xs text-gray-500">1–24 hrs processing · {settings.manual_payment_fee_percentage * 100}% fee</p>
                  </div>
                  {method === 'manual' && <CheckCircle size={18} className="text-blue-600 shrink-0" />}
                </button>
              )}
              
              <div className="rounded-xl bg-blue-50 border border-blue-100 p-3 text-xs text-blue-700">
                <span className="font-semibold">Fee: {(currentFeePercentage * 100).toFixed(1)}%</span> · You pay <span className="font-bold">GH₵{totalAmountToPay.toFixed(2)}</span> · Wallet credited <span className="font-bold">GH₵{finalAmount.toFixed(2)}</span>
              </div>

              {method === 'online' && settings.pay_online_enabled && (
                <button onClick={handleOnlinePayment}
                  className="w-full py-3 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2 mt-2"
                  style={{ background: 'linear-gradient(135deg,#1a56db,#6c3afc)' }}>
                  Pay GH₵{totalAmountToPay.toFixed(2)} <ArrowRight size={16} />
                </button>
              )}
              {method === 'manual' && settings.manual_payment_enabled && (
                <button onClick={() => setStep('manual')}
                  className="w-full py-3 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2 mt-2"
                  style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)' }}>
                  See Bank Details <ArrowRight size={16} />
                </button>
              )}
            </div>
          )}

          {/* STEP: manual */}
          {step === 'manual' && (
            <form onSubmit={handleManualSubmit} className="space-y-4">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => setStep('method')} className="text-sm text-blue-600 hover:underline">← Back</button>
                </div>
                <div className="text-sm font-bold text-indigo-700">Send: GH₵{totalAmountToPay.toFixed(2)}</div>
              </div>
              
              <div className="space-y-1">
                <label className="block text-xs font-semibold text-gray-600">Select Transfer Method</label>
                <select 
                    value={selectedBankOptionId} 
                    onChange={e => setSelectedBankOptionId(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                    {settings.manual_bank_options.map(opt => (
                        <option key={opt.id} value={opt.id}>{opt.label}</option>
                    ))}
                </select>
              </div>

              {selectedBankOption && (
                <div className="rounded-xl bg-indigo-50 border border-indigo-100 p-4 text-sm space-y-1">
                  <p className="font-bold text-indigo-900 mb-2 text-center text-xs uppercase tracking-wider">{selectedBankOption.label} Details</p>
                  
                  {selectedBankOption.type === 'bank' && (
                    <>
                        <p className="text-indigo-800"><span className="text-gray-500">Bank:</span> <strong>{selectedBankOption.bankName}</strong></p>
                        <p className="text-indigo-800"><span className="text-gray-500">Account:</span> <strong className="font-mono">{selectedBankOption.accountNumber}</strong></p>
                        <p className="text-indigo-800"><span className="text-gray-500">Name:</span> <strong>{selectedBankOption.accountName}</strong></p>
                        <p className="text-indigo-800"><span className="text-gray-500">Branch:</span> {selectedBankOption.branch}</p>
                    </>
                  )}
                  
                  {(selectedBankOption.type === 'momopay' || selectedBankOption.type === 'manual_momo') && (
                    <>
                        <p className="text-indigo-800"><span className="text-gray-500">Code/Number:</span> <strong className="font-mono text-base">{selectedBankOption.merchantId || selectedBankOption.merchantNumber}</strong></p>
                        <p className="text-indigo-800"><span className="text-gray-500">Name:</span> <strong>{selectedBankOption.accountName}</strong></p>
                        <p className="text-indigo-800"><span className="text-gray-500">Steps:</span> {selectedBankOption.instructions}</p>
                    </>
                  )}
                  
                  <div className="pt-2 mt-2 border-t border-indigo-100">
                    <p className="text-indigo-900 font-medium text-center">Amount to Transfer: <strong className="text-lg">GH₵{totalAmountToPay.toFixed(2)}</strong></p>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Transaction Reference (From receipt) *</label>
                  <input required value={manualRef} onChange={e => setManualRef(e.target.value)}
                    placeholder="e.g. TRF20240428123"
                    className="w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Transfer Date *</label>
                  <input required type="date" value={manualDate} onChange={e => setManualDate(e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div className="flex items-start gap-2 text-xs text-gray-500 bg-gray-50 rounded-lg p-3">
                <AlertCircle size={14} className="shrink-0 mt-0.5 text-amber-500" />
                We'll verify your transfer of GH₵{totalAmountToPay.toFixed(2)} within 24 hours and credit your wallet automatically.
              </div>
              <button type="submit" disabled={loading}
                className="w-full py-3 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2"
                style={{ background: loading ? '#d1d5db' : 'linear-gradient(135deg,#6366f1,#8b5cf6)' }}>
                {loading ? <><Loader2 size={16} className="animate-spin" /> Submitting...</> : 'Confirm Transfer Made'}
              </button>
            </form>
          )}

          {/* STEP: processing */}
          {step === 'processing' && (
            <div className="py-10 flex flex-col items-center gap-4">
              <Loader2 size={40} className="animate-spin text-blue-600" />
              <p className="text-sm font-semibold text-gray-700">Opening payment gateway…</p>
              <p className="text-xs text-gray-400 text-center">Complete the payment in the popup window. Do not close this tab.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
